/**
 * PortfolioReport — read-only Portfolio Health Report page.
 *
 * Internal-staff-only view over GET /api/portfolio/report/latest, with a
 * manual "Regenerate" trigger via POST /api/portfolio/report/trigger. All
 * data-fetching, polling and regenerate-race handling lives in
 * `usePortfolioReport` — this component is purely a state → UI mapping.
 *
 * See packages/web/src/__tests__/unit/PortfolioReport.test.tsx for the full
 * render contract.
 */

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { usePortfolioReport } from "../hooks/usePortfolioReport.js";
import type { PortfolioProjectCard, PortfolioRollup } from "../types/portfolio.js";

// ---------------------------------------------------------------------------
// Local helpers — safe extraction from the deliberately `unknown` metrics/deltas
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

interface ProjectMetricsView {
	tasksOpen: number | null;
	tasksOverdue: number | null;
	tasksCompletedThisWeek: number | null;
	milestonesOverdue: number | null;
}

function readMetrics(metrics: unknown): ProjectMetricsView {
	const root = isPlainObject(metrics) ? metrics : {};
	const tasks = isPlainObject(root.tasks) ? root.tasks : {};
	const milestones = isPlainObject(root.milestones) ? root.milestones : {};
	return {
		tasksOpen: safeNumber(tasks.open),
		tasksOverdue: safeNumber(tasks.overdue),
		tasksCompletedThisWeek: safeNumber(tasks.completedThisWeek),
		milestonesOverdue: safeNumber(milestones.overdue),
	};
}

const SENTIMENT_TONE: Record<string, "ok" | "warn" | "err" | undefined> = {
	positive: "ok",
	neutral: undefined,
	negative: "err",
};

function sentimentRiskWeight(label: string): number {
	const normalised = label.toLowerCase();
	if (normalised === "negative") return 3;
	if (normalised === "positive") return 0;
	return 1; // neutral / unknown
}

/**
 * Higher score = higher risk. Weighs overdue milestones heaviest, then
 * overdue tasks, then sentiment — deliberately simple and legible rather
 * than a precisely-tuned model; the UI contract only requires the ordering
 * to be directionally correct for an unambiguous worse-on-every-signal case.
 */
function riskScore(project: PortfolioProjectCard): number {
	const metrics = readMetrics(project.metrics);
	const overdueTasks = metrics.tasksOverdue ?? 0;
	const overdueMilestones = metrics.milestonesOverdue ?? 0;
	const sentiment = sentimentRiskWeight(project.sentiment.label);
	return overdueMilestones * 5 + overdueTasks * 2 + sentiment * 4;
}

function sortByRisk(projects: PortfolioProjectCard[]): PortfolioProjectCard[] {
	return [...projects].sort((a, b) => riskScore(b) - riskScore(a));
}

/**
 * Friendly British date/time formatting for the footer — deliberately NOT the
 * raw ISO string, so it never collides with the header's raw `report_date`
 * text when both happen to fall on the same calendar day.
 */
function formatDateTime(iso: string): string {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return iso;
	return date.toLocaleString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function statusChipTone(status: string): "ok" | "warn" | "err" | undefined {
	if (status === "complete") return "ok";
	if (status === "failed") return "err";
	if (IN_PROGRESS_LABELS.has(status)) return "warn";
	return undefined;
}

const IN_PROGRESS_LABELS = new Set([
	"pending",
	"aggregating",
	"collecting",
	"computing",
	"generating",
]);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RegenerateButton({
	onRegenerate,
	disabled,
}: {
	onRegenerate: () => void;
	disabled: boolean;
}) {
	return (
		<button
			type="button"
			className="portfolio-regenerate-btn"
			onClick={onRegenerate}
			disabled={disabled}
		>
			<span className={`portfolio-regenerate-spinner${disabled ? " portfolio-regenerate-spinner--active" : ""}`} aria-hidden="true" />
			Regenerate
		</button>
	);
}

function KpiRow({ rollup }: { rollup: PortfolioRollup }) {
	const { kpis } = rollup;
	return (
		<div className="portfolio-kpi-row">
			<div className="portfolio-kpi-tile">
				<span className="portfolio-kpi-tile-value tnum">{kpis.totalTasksOpen}</span>
				<span className="portfolio-kpi-tile-label">Open tasks</span>
			</div>
			<div
				className={`portfolio-kpi-tile${kpis.totalTasksOverdue > 0 ? " portfolio-kpi-tile--warn" : ""}`}
			>
				<span className="portfolio-kpi-tile-value tnum">{kpis.totalTasksOverdue}</span>
				<span className="portfolio-kpi-tile-label">Overdue tasks</span>
			</div>
			<div className="portfolio-kpi-tile portfolio-kpi-tile--ok">
				<span className="portfolio-kpi-tile-value tnum">{kpis.totalTasksCompletedThisWeek}</span>
				<span className="portfolio-kpi-tile-label">Closed this week</span>
			</div>
			<div
				className={`portfolio-kpi-tile${kpis.totalMilestonesOverdue > 0 ? " portfolio-kpi-tile--warn" : ""}`}
			>
				<span className="portfolio-kpi-tile-value tnum">{kpis.totalMilestonesOverdue}</span>
				<span className="portfolio-kpi-tile-label">Overdue milestones</span>
			</div>
			<div className="portfolio-kpi-tile">
				<span className="portfolio-kpi-tile-value tnum">
					{rollup.projectsIncluded} of {rollup.projectsTotal}
				</span>
				<span className="portfolio-kpi-tile-label">Projects covered</span>
			</div>
		</div>
	);
}

function ProjectCard({ project }: { project: PortfolioProjectCard }) {
	const metrics = readMetrics(project.metrics);
	const tone = SENTIMENT_TONE[project.sentiment.label.toLowerCase()];
	const atRisk = (metrics.tasksOverdue ?? 0) > 0 || (metrics.milestonesOverdue ?? 0) > 0;

	return (
		<details
			className="portfolio-project-card"
			data-project-id={project.projectId}
			open={atRisk}
		>
			<summary className="portfolio-project-card-header">
				<h3 className="portfolio-project-card-name">{project.projectName}</h3>
				<span className={`chip${tone ? ` ${tone}` : ""}`}>{project.sentiment.label || "unknown"}</span>
			</summary>
			<div className="portfolio-project-card-body">
				{project.scope && <p className="portfolio-project-card-scope">{project.scope}</p>}
				<dl className="portfolio-project-card-metrics">
					<div>
						<dt>Open</dt>
						<dd className="tnum">{metrics.tasksOpen ?? "—"}</dd>
					</div>
					<div>
						<dt>Overdue</dt>
						<dd className="tnum">{metrics.tasksOverdue ?? "—"}</dd>
					</div>
					<div>
						<dt>Closed</dt>
						<dd className="tnum">{metrics.tasksCompletedThisWeek ?? "—"}</dd>
					</div>
					<div>
						<dt>Milestones overdue</dt>
						<dd className="tnum">{metrics.milestonesOverdue ?? "—"}</dd>
					</div>
				</dl>
				{project.sentiment.rationale && (
					<p className="portfolio-project-card-rationale dim">{project.sentiment.rationale}</p>
				)}
			</div>
		</details>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PortfolioReport(): JSX.Element {
	const { state, isRegenerating, regenerate, refetch } = usePortfolioReport();

	const handleRegenerate = (): void => {
		void regenerate();
	};

	if (state.status === "loading") {
		return (
			<main className="portfolio-page" aria-busy="true">
				<div className="portfolio-header">
					<h1>Portfolio Health Report</h1>
				</div>
				<div className="portfolio-kpi-row" aria-hidden="true">
					{Array.from({ length: 5 }).map((_, i) => (
						<div key={i} className="portfolio-kpi-tile skeleton" style={{ height: "72px" }} />
					))}
				</div>
			</main>
		);
	}

	if (state.status === "denied") {
		const message =
			state.deniedStatus === 401
				? "You are not signed in. Please sign in to view this page."
				: "This page is restricted to internal staff.";
		return (
			<main className="portfolio-page">
				<div className="portfolio-header">
					<h1>Portfolio Health Report</h1>
				</div>
				<div role="alert" className="portfolio-message portfolio-message--denied">
					<h2>Access denied</h2>
					<p>{message}</p>
				</div>
			</main>
		);
	}

	if (state.status === "empty") {
		return (
			<main className="portfolio-page">
				<div className="portfolio-header">
					<h1>Portfolio Health Report</h1>
				</div>
				<div className="portfolio-message portfolio-message--empty">
					<p>No report has been generated yet. Use Regenerate to create the first one.</p>
					<RegenerateButton onRegenerate={handleRegenerate} disabled={isRegenerating} />
				</div>
			</main>
		);
	}

	if (state.status === "error") {
		return (
			<main className="portfolio-page">
				<div className="portfolio-header">
					<h1>Portfolio Health Report</h1>
				</div>
				<div role="alert" className="portfolio-message portfolio-message--error">
					<h2>Unable to load the report</h2>
					<p>{state.message}</p>
					<button type="button" className="portfolio-retry-btn" onClick={refetch}>
						Try again
					</button>
				</div>
			</main>
		);
	}

	// --- success ---
	const { report, rollup, inProgress, pollTimedOut } = state;
	const sortedProjects = rollup ? sortByRisk(rollup.projects) : [];
	const chipTone = statusChipTone(report.generation_status);

	return (
		<main className="portfolio-page">
			<div className="portfolio-header">
				<div className="portfolio-header-titles">
					<h1>Portfolio Health Report</h1>
					<p className="portfolio-header-meta">
						Report date: <span className="tnum">{report.report_date}</span>
						{" · "}
						<span className={`chip${chipTone ? ` ${chipTone}` : ""}`}>
							{report.generation_status}
						</span>
					</p>
				</div>
				<RegenerateButton onRegenerate={handleRegenerate} disabled={isRegenerating} />
			</div>

			{inProgress && (
				<div role="status" className="portfolio-banner portfolio-banner--progress">
					Report is being generated… this page will refresh automatically.
				</div>
			)}

			{pollTimedOut && (
				<p className="portfolio-banner portfolio-banner--timeout">
					Still generating — this is taking longer than usual. Please check back shortly.
				</p>
			)}

			<section className="portfolio-narrative">
				<h2>Summary</h2>
				{report.ai_narrative ? (
					<div className="portfolio-narrative-body">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{report.ai_narrative}</ReactMarkdown>
					</div>
				) : (
					<p className="portfolio-narrative-empty dim">AI narrative not yet available.</p>
				)}
			</section>

			{rollup === null ? (
				<p className="portfolio-message portfolio-message--unavailable">
					Portfolio data unavailable — the stored report could not be read.
				</p>
			) : (
				<>
					<KpiRow rollup={rollup} />

					<section className="portfolio-projects">
						<h2>Projects</h2>
						<div className="portfolio-project-grid">
							{sortedProjects.map((project) => (
								<ProjectCard key={project.projectId} project={project} />
							))}
						</div>
					</section>

					{rollup.skipped.length > 0 && (
						<details className="portfolio-skipped">
							<summary>Skipped projects ({rollup.skipped.length})</summary>
							<ul>
								{rollup.skipped.map((skipped) => (
									<li key={skipped.projectId}>
										<strong>{skipped.projectName}</strong> — {skipped.reason}
									</li>
								))}
							</ul>
						</details>
					)}
				</>
			)}

			<footer className="portfolio-footer dim">
				<p>
					{report.fortnight_index !== null ? <>Fortnight #{report.fortnight_index} · </> : null}Generated{" "}
					{formatDateTime(rollup?.generatedAt || report.created_at)} · Last updated{" "}
					{formatDateTime(report.updated_at)}
				</p>
			</footer>
		</main>
	);
}
